import { Link } from 'react-router-dom'
import { Plane, MapPin, Shield, Clock, Users, Star, CheckCircle, ArrowRight, Car } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import Seo from '../components/Seo'

export default function Services() {

  const services = [
    {
      id: 'flights',
      title: 'Flight Bookings',
      icon: Plane,
      description: 'Expert flight booking services with access to the best deals and routes worldwide.',
      features: [
        'International & domestic flights',
        'Multi-city itineraries',
        'Group bookings',
        'Last-minute deals',
        '24/7 support',
        'Best price guarantee'
      ],
      image: '/6c3c1580-al-AA-55b57246.jpg',
      price: 'From R500 booking fee'
    },
    {
      id: 'day-trips',
      title: 'Day Trips & Excursions',
      icon: MapPin,
      description: 'Curated day trips and local excursions to make the most of your destination.',
      features: [
        'Local guided tours',
        'Cultural experiences',
        'Adventure activities',
        'Food & wine tours',
        'Photography tours',
        'Custom itineraries'
      ],
      image: '/viator-social-preview.png',
      price: 'From R800 per person'
    },
    {
      id: 'travel-insurance',
      title: 'Travel Insurance',
      icon: Shield,
      description: 'Comprehensive travel insurance coverage for peace of mind on your journey.',
      features: [
        'Medical coverage',
        'Trip cancellation',
        'Baggage protection',
        'Emergency assistance',
        'Adventure sports coverage',
        'Family plans available'
      ],
      image: '/s-ytHrVH_400x400.jpg',
      price: 'From R150 per person'
    },
    {
      id: 'shuttle-services',
      title: 'Shuttle Services',
      icon: Car,
      description: 'Reliable shuttle services for seamless airport transfers.',
      features: [
        'Airport transfers in Durban',
        'Local city tours',
        'Hotel pickups & drop-offs',
        'International partner network',
        'Comfortable modern vehicles',
        'Professional drivers'
      ],
      image: '/ff.jpg',
      price: 'From R200 per trip'
    }
  ]

  return (
    <div className="bg-white">
      <Seo
        title="Travel Services - Memento Travel Co."
        description="Comprehensive travel services including custom itineraries, luxury accommodations, adventure tours, and expert destination guidance."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.mementotravel.co/services'}
        pageType="services"
      />
      <PageHeader
        title="Our Services"
        description="Comprehensive travel services designed to make your journey seamless and unforgettable."
      />
      
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Services Overview */}
          <div className="mx-auto max-w-3xl text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
              Travel Services That <span style={{color: '#006D77'}}>Exceed Expectations</span>
            </h2>
            <p className="text-lg text-gray-700">
              From flight bookings to travel insurance, we provide comprehensive travel services 
              that ensure your journey is smooth, safe, and memorable. Our experienced team handles 
              every detail so you can focus on creating unforgettable experiences.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8 mb-20">
            {services.map((service) => {
              const IconComponent = service.icon
              return (
                <div key={service.id} id={service.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="relative h-48">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <div 
                        className="flex h-12 w-12 items-center justify-center rounded-lg"
                        style={{ background: '#006D77' }}
                      >
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-medium text-lg drop-shadow">{service.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.description}</p>
                    <div className="space-y-2">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    {service.id !== 'shuttle-services' && (
                      <div className="mt-6 flex justify-center">
                        <a 
                          href={
                            service.id === 'flights'
                              ? '/flights'
                              : service.id === 'day-trips' 
                              ? 'https://www.viator.com/en-ZA/Bali/d98-ttd?pid=P00165007&uid=U00478366&mcid=58086&currency=ZAR'
                              : service.id === 'travel-insurance'
                              ? '/contact'
                              : `/services/${service.id}`
                          }
                          target={service.id === 'day-trips' ? '_blank' : '_self'}
                          rel={service.id === 'day-trips' ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-md hover:opacity-90 transition-all duration-200 font-medium"
                          style={{background: '#006D77'}}
                        >
                          Explore <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Why Choose Our Services */}
          <div className="bg-[#F7F7E9] rounded-3xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                Why Choose <span style={{color: '#006D77'}}>Memento Travel</span> Services?
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                We combine industry expertise with personalized service to deliver travel solutions 
                that exceed your expectations every time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
                <p className="text-sm text-gray-600">Round-the-clock assistance whenever you need it</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Knowledge</h3>
                <p className="text-sm text-gray-600">Years of experience in travel planning and booking</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalized Service</h3>
                <p className="text-sm text-gray-600">Tailored solutions for your unique travel needs</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Reliable</h3>
                <p className="text-sm text-gray-600">Protected bookings with trusted partners</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Let our travel experts help you plan the perfect trip. Contact us today for a 
              personalized consultation and quote.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/#contact"
                className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg shadow-lg hover:opacity-90 transition-all duration-200 font-medium"
                style={{background: '#006D77'}}
                onClick={() => {
                  setTimeout(() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
              >
                Get Started <ArrowRight className="h-5 w-5" />
              </Link>
              <a 
                href="/packages"
                className="inline-flex items-center gap-2 text-gray-700 border-2 border-gray-300 px-8 py-4 rounded-lg hover:border-gray-400 transition-colors font-medium"
              >
                View Packages <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
